import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ShopSearchParametersModalConfig as ShopSearchParametersModalContentConfig } from '@front-platform/content-builder';
import { shopSearchParametersModalResolveConfig } from '@front-platform/content-builder';
import { Component, OnInit, input } from '@angular/core';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import { find } from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { DistributionModeKeys } from '@config/keys/shop.keys';

import { OrderTypeValues } from '@config/values/order.values';

import { DoodDeliveryAddressModel } from '@core/models/delivery-address.model';
import { PlaceResult } from '@core/models/google-maps.model';
import { ILocationParameter } from '@core/models/parameters.model';
import { IDistributionMode } from '@core/models/shop.model';
import { ISimpleItem } from '@core/models/simple-item.model';
import { DoodWantedAtType } from '@store/settings/settings.model';

import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';

import { DateUtils } from '@shared/utils/date/date.utils';
import { LocationUtils } from '@shared/utils/location/location.utils';
import { ParametersUtils } from '@shared/utils/parameters/parameters.utils';
import { ITimeSlot } from '@shared/utils/time-slots/time-slots.utils';

import { IOrderTypeCapabilities } from '@config/order-types-capabilities.config';
import { ModalScrollBlockBase } from '@core/base/modalScrollBlock/modal-scroll-block.base';
import { SettingsParametersState } from '@store/settings';

import { CartStoreDispatcher } from '@common/dispatchers/cart.dispatcher';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { BASE_CUSTOM_SWITCH_VALUES } from '@config/values/parameters.values';
import { ModalConfig } from '@core/services/modals/modals-util.type';
import { ModalsService } from '@core/services/modals/modals.service';
import { OrderTypeService } from '@core/services/order-type/order-type.service';
import { OrdersService } from '@core/services/orders/orders.service';
import { ShopSearchParametersService } from '@core/services/shop-search-parameters/shop-search-parameters.service';

export type ShopSearchParametersModalConfig = ModalConfig<{
  handle: 'shop-search-parameters-modal';
  inputs: ShopSearchParametersModalInputs;
  outputs: ShopSearchParametersModalEvents;
}>;
type ShopSearchParametersModalInputs = {
  overrideSelectedDistributionMode?: string;
};

type ShopSearchParametersModalEvents = {
  type: 'updated';
  success: boolean;
};
export interface ShopSearchParametersModalRuntimeConfig {}

export type ShopSearchParametersModalConfigInput = Partial<
  Omit<ShopSearchParametersModalContentConfig, keyof ShopSearchParametersModalRuntimeConfig> &
    ShopSearchParametersModalRuntimeConfig
>;
export type ShopSearchParametersModalResolvedConfig = Omit<
  ShopSearchParametersModalContentConfig,
  keyof ShopSearchParametersModalRuntimeConfig
> &
  Partial<ShopSearchParametersModalRuntimeConfig>;
export interface ShopSearchParametersModalContract extends ConfigComponentContract<
  ShopSearchParametersModalConfigInput,
  ShopSearchParametersModalResolvedConfig
> {}

@Component({
  selector: 'app-shop-search-parameters-modal',
  templateUrl: './shop-search-parameters-modal.component.html',
  standalone: false,
})
export class ShopSearchParametersModalComponent
  extends ModalScrollBlockBase
  implements OnInit, ShopSearchParametersModalContract
{
  readonly config = input<ShopSearchParametersModalConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    ShopSearchParametersModalContentConfig,
    ShopSearchParametersModalRuntimeConfig
  >(shopSearchParametersModalResolveConfig, () => ({ config: this.config() }));
  static handle = 'shop-search-parameters-modal' as const;

  isMobile$ = this.settingsSelector.selectDeviceIsMobileScreen;

  index$: Observable<number> = this.modalSelector
    .selectModal(ShopSearchParametersModalComponent.handle)
    .pipe(map(el => (el?.index ? el.index : 4)));

  data$: Observable<ShopSearchParametersModalInputs> = this.modalSelector
    .selectModal(ShopSearchParametersModalComponent.handle)
    .pipe(map(el => (el?.data ? el.data : {})));

  get displayAsapForLaterPicker(): boolean {
    return this.resolvedConfig()['display_asap_for_later_picker'] as unknown as boolean;
  }

  distributionModeItems: ISimpleItem[] = BASE_CUSTOM_SWITCH_VALUES;
  selectedOrderTypeCapabilities!: IOrderTypeCapabilities;

  // @fixme default value
  selectedDistributionMode = 'click-and-collect';
  selectedLocation?: string;
  selectedWantedAtType!: DoodWantedAtType;
  selectedWantedAtDate?: Date;
  selectedWantedAtTime?: Date;
  wantedAtDateSlots?: Date[];
  wantedAtTimeSlots?: ITimeSlot[];
  message?: string;
  distributionMode = OrderTypeValues;
  deliveryComment?: string | null;
  instructions$ = this.cartSelector.selectActive$.pipe(
    map(cart => cart?.delivery_address?.instructions),
  );

  private newSelectedLocation?: ILocationParameter;
  private intervalMinuteTimeSlot = 15;
  private previousSelectedWantedAtTime?: Date;
  private allWantedAtTimeSlots: ITimeSlot[] = [];

  public readonly userIsLoggedIn$ = this.authSelector.selectUserIsLoggedIn;

  constructor(
    private cartSelector: CartStoreSelector,
    private modalSelector: ModalStoreSelector,
    private cartDispatcher: CartStoreDispatcher,
    private readonly modalsService: ModalsService<ShopSearchParametersModalConfig>,
    private readonly ordersService: OrdersService,
    private settingsSelector: SettingsStoreSelector,
    private readonly orderTypeService: OrderTypeService,
    private readonly marketplaceSelector: MarketplaceStoreSelector,
    private readonly shopSearchParametersService: ShopSearchParametersService,
    private readonly authSelector: AuthStoreSelector,
  ) {
    super();
    dayjs.extend(isToday);
    dayjs.extend(isTomorrow);
  }

  ngOnInit(): void {
    const savedParameters = this.shopSearchParametersService.getParameters();
    this.data$.subscribe(data => {
      this.selectedDistributionMode =
        data.overrideSelectedDistributionMode ?? savedParameters.distribution_mode;
    });
    this.selectedLocation = savedParameters.location?.address;
    this.initSelectedWantedAt(savedParameters.wanted_at);
    this.updateSelectedOrderTypeCapabilities();
    this.updateWantedAtDateSlots();
    this.setAllowedMarketplaceDistributionsMode();
    this.updateMessage();
  }

  close(): void {
    this.modalsService.close(ShopSearchParametersModalComponent.handle);
  }

  save(): void {
    const toSave: Partial<SettingsParametersState> = {
      is_distribution_mode_defined: true,
      distribution_mode: this.selectedDistributionMode,
    };

    if (this.newSelectedLocation) {
      toSave.location = this.newSelectedLocation;
      if (this.cartSelector.active) {
        let deliveryAddress = this.ordersService.placeResultToDeliveryAddress(
          this.newSelectedLocation as PlaceResult,
        );

        if (this.deliveryComment && deliveryAddress) {
          deliveryAddress.instructions = this.deliveryComment;
        }

        this.cartDispatcher.updateActive({
          delivery_address: deliveryAddress,
        });
      }
      this.modalsService.emitEvent({
        data: { type: 'updated', success: true },
        handle: ShopSearchParametersModalComponent.handle,
      });
    }

    if (
      this.selectedOrderTypeCapabilities?.preorderingAllowed &&
      this.selectedWantedAtType === DoodWantedAtType.later
    ) {
      toSave.wanted_at = this.selectedWantedAtTime;
    } else {
      toSave.wanted_at = null;
    }

    this.shopSearchParametersService.set(toSave);
    if (this.selectedDistributionMode !== 'delivery') {
      this.modalsService.emitEvent({
        data: { type: 'updated', success: true },
        handle: ShopSearchParametersModalComponent.handle,
      });
    }
    this.close();
  }

  distributionModeSelectionChanged($event: string): void {
    this.selectedDistributionMode = $event;
    this.updateSelectedOrderTypeCapabilities();
    this.updateWantedAtDateSlots();
    this.updateMessage();
  }

  locationChanged(locationValue: Partial<DoodDeliveryAddressModel>): void {
    const location = locationValue as DoodDeliveryAddressModel;
    const locationParameters = LocationUtils.mapDeliveryAddressToLocation(location);
    this.newSelectedLocation = locationParameters;
    this.selectedLocation = locationParameters?.address;
  }

  deliveryCommentChanged(instructions?: string): void {
    this.deliveryComment = instructions;
  }

  private updateSelectedOrderTypeCapabilities(): void {
    this.selectedOrderTypeCapabilities = this.orderTypeService.getCapabilities(
      this.selectedDistributionMode,
    );
  }

  onWantedAtTypeChanged(wantedAtType: DoodWantedAtType): void {
    this.selectedWantedAtType = wantedAtType;
    this.shopSearchParametersService.set({
      wanted_at_type: wantedAtType,
    });
    this.updateWantedAtDateSlots();
  }

  onWantedAtDateChanged(wantedAtDate?: Date): void {
    this.selectedWantedAtDate = wantedAtDate;
    this.previousSelectedWantedAtTime = this.selectedWantedAtTime;
    this.selectedWantedAtTime = undefined;
    this.updateWantedAtTimeSlots(); // local filtering only
  }

  onWantedAtTimeChanged(wantedAtTime?: Date): void {
    this.selectedWantedAtTime = wantedAtTime;
  }

  private updateWantedAtDateSlots(): void {
    if (this.selectedWantedAtType === DoodWantedAtType.asap) {
      this.wantedAtDateSlots = [];
      this.wantedAtTimeSlots = undefined;
      this.allWantedAtTimeSlots = [];
      return;
    }

    if (!this.selectedOrderTypeCapabilities?.preorderingAllowed) {
      this.wantedAtDateSlots = [];
      this.wantedAtTimeSlots = undefined;
      this.allWantedAtTimeSlots = [];
      return;
    }

    this.loadAllWantedAtSlotsFromApi();
  }

  private updateWantedAtTimeSlots(): void {
    this.wantedAtTimeSlots = undefined;

    if (
      this.selectedWantedAtType === DoodWantedAtType.asap ||
      !this.selectedOrderTypeCapabilities?.preorderingAllowed
    ) {
      return;
    }

    if (!this.selectedWantedAtDate) {
      this.selectedWantedAtTime = undefined;
      return;
    }

    const selectedDay = DateUtils.dayjsInMarketplaceTimezone(this.selectedWantedAtDate);
    const daySlots = this.allWantedAtTimeSlots
      .filter(slot => slot.start.isSame(selectedDay, 'day'))
      .map((slot, idx) => ({
        ...slot,
        id: idx + 1,
      }));

    this.wantedAtTimeSlots = daySlots;

    if (!this.wantedAtTimeSlots.length) {
      this.selectedWantedAtTime = undefined;
      return;
    }

    this.selectedWantedAtTime = new Date(this.wantedAtTimeSlots[0].start.format());

    if (this.previousSelectedWantedAtTime) {
      const selectedSlot = find(
        this.wantedAtTimeSlots,
        slot =>
          slot.start.format('HH:mm:ss') ===
          DateUtils.dayjsInMarketplaceTimezone(this.previousSelectedWantedAtTime).format(
            'HH:mm:ss',
          ),
      );
      if (selectedSlot) {
        this.selectedWantedAtTime = new Date(selectedSlot.start.format());
      }
    }
  }

  private loadAllWantedAtSlotsFromApi(): void {
    const cart = this.cartSelector.active;

    this.wantedAtDateSlots = undefined;
    this.wantedAtTimeSlots = undefined;

    this.ordersService
      .getOrderSlots$(cart, null, this.selectedDistributionMode)
      .pipe(
        map(slots => this.mapUnixSlotsToTimeSlots(slots)),
        map(timeSlots => {
          this.allWantedAtTimeSlots = timeSlots;

          const daysMap = new Map<string, Date>();
          for (const slot of timeSlots) {
            const day = new Date(slot.start.startOf('day').format());
            const key = DateUtils.dayjsInMarketplaceTimezone(day).format('YYYY-MM-DD');
            if (!daysMap.has(key)) {
              daysMap.set(key, day);
            }
          }

          return [...daysMap.values()].sort((a, b) => a.getTime() - b.getTime());
        }),
        catchError(() => {
          this.allWantedAtTimeSlots = [];
          return of([] as Date[]);
        }),
      )
      .subscribe(dateSlots => {
        this.wantedAtDateSlots = dateSlots;

        if (!this.wantedAtDateSlots.length) {
          this.selectedWantedAtDate = undefined;
          this.selectedWantedAtTime = undefined;
          this.wantedAtTimeSlots = [];
          return;
        }

        const selectedDateStillAvailable = this.selectedWantedAtDate
          ? this.wantedAtDateSlots.some(d =>
              DateUtils.dayjsInMarketplaceTimezone(d).isSame(this.selectedWantedAtDate, 'day'),
            )
          : false;

        if (!selectedDateStillAvailable) {
          this.selectedWantedAtDate = this.wantedAtDateSlots[0];
        }

        this.updateWantedAtTimeSlots();
      });
  }

  private mapUnixSlotsToTimeSlots(slots: number[]): ITimeSlot[] {
    return [...slots]
      .filter((slot): slot is number => typeof slot === 'number' && Number.isFinite(slot))
      .sort((a, b) => a - b)
      .map((slot, idx) => {
        const start = DateUtils.dayjsInMarketplaceTimezone(slot * 1000);
        return {
          id: idx + 1,
          start,
          end: start.add(15, 'minute'),
        };
      });
  }

  private initSelectedWantedAt(wantedAt: Date | null): void {
    if (!wantedAt) {
      this.selectedWantedAtType = DoodWantedAtType.asap;
      this.shopSearchParametersService.set({
        wanted_at_type: DoodWantedAtType.asap,
      });
      return;
    }

    this.selectedWantedAtType = DoodWantedAtType.later;
    this.shopSearchParametersService.set({
      wanted_at_type: DoodWantedAtType.later,
    });
    this.selectedWantedAtDate = wantedAt;
    this.previousSelectedWantedAtTime = wantedAt;
    this.selectedWantedAtTime = wantedAt;
  }

  private updateMessage(): void {
    const currentDistributionMode = this.distributionModeItems?.find(
      distributionMode => distributionMode.value === this.selectedDistributionMode,
    );
    this.message = undefined;
    if (currentDistributionMode) {
      this.message = currentDistributionMode.message;
    }
  }

  private setAllowedMarketplaceDistributionsMode(): void {
    this.marketplaceSelector.selectMarketplaceDistributionModes
      .pipe(map(distributionModes => this.allowedDistributionModesLoaded(distributionModes)))
      .subscribe();
  }

  private allowedDistributionModesLoaded(distributionModes: IDistributionMode[]): void {
    this.distributionModeItems = ParametersUtils.mapDistributionModes(
      distributionModes
        .filter(item => item[DistributionModeKeys.Enabled])
        .sort((a, b) => a[DistributionModeKeys.Priority] - b[DistributionModeKeys.Priority]),
    );

    if (this.distributionModeItems.length === 1) {
      this.selectedDistributionMode = this.distributionModeItems[0].value;
      this.updateSelectedOrderTypeCapabilities();
    }
    if (
      this.distributionModeItems.length === 1 &&
      this.selectedDistributionMode === OrderTypeValues.EatIn
    ) {
      this.close();
    }
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ShopSearchParametersModalComponent, {
  'keyAliases': {},
});
